import React, { useEffect, useState } from 'react';
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from '@mui/material/Button';
import { Card, Divider, Layout, Col, Image, Space, Row, Typography, Result, Tabs, Drawer, Grid } from 'antd';
import {WarningSharp, SchoolRounded} from "@mui/icons-material";
import { fetchPortfolioPage } from "../../api/publicApi";
import { fetchDetailedPortfolioObjects } from "../../api/portfolioApi";
import { selectFullPortfolios } from "../../store/slices/portfolios.slice";
import {Brand} from "../../components/Graphics/Brand";
import Disclaimer from "../../layouts/Disclaimer";
import {default as BottomDisclaimer} from "../../components/Disclaimer/Disclaimer";
import {markDisclaimerAsSeen, setEmail, setPublicAccess} from "../../store/slices/public.slice";
import { GoogleCloudStorageBucketUrl } from '../../configs/Domain';
import {fetchTouchpoints} from "../../api/userApi";
import ThumbnailGrid from "../../components/ThumbnailGrid/ThumbnailGrid";
import {filterAppsForUser} from "../../configs/AppsConfig";
import {isSessionActive, saveAccessInfo} from "./publicAuthUtils";
import Cheatsheet from "../Portfolio/Cheatsheet";
import PublicHeader from "../../layouts/PublicHeader";
import WidgetContainer from "../../components/WidgetContainer";
import PublicEntry from "../Entry/PublicEntry";
const DefaultDescription = `This IQ page has been created to showcase the portfolios hand selected by the manager 
who sent you this page.`;

const { useBreakpoint } = Grid;

const PortfolioPagePublicEntrypoint = () => {
    const { pageId } = useParams();
    const screens = useBreakpoint();
    const dispatch = useDispatch();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const disclaimerConfirmed = useSelector(state => state.public.disclaimerSeen);
    const email = useSelector(state => state.public.email);
    const portfolioPage = useSelector(state => state?.public?.portfolioPage);
    const portfolioIds = portfolioPage?.portfolio_ids;
    const touchpointIds = portfolioPage?.touchpoint_ids;
    const fullPortfolios = useSelector((state)=>selectFullPortfolios(state, portfolioIds||[]));
    const files = portfolioPage?.files || [];
    const apps = portfolioPage?.apps || [];
    const brandUrl = portfolioPage?.brand;
    const filteredApps = apps.length > 0 && filterAppsForUser(apps, '/public');
    const [showSide, setShowSide] = useState(false);

    useEffect(()=>{
        let stringEmail = email;
        if (email){
            if (email.hasOwnProperty('email')){
                stringEmail = email.email;
            }
            dispatch(fetchPortfolioPage({ pageId, email: stringEmail }));
        }
    }, [email])

    useEffect(()=>{
        if (portfolioIds){
            dispatch(fetchDetailedPortfolioObjects(portfolioIds));
        }
    }, [portfolioIds]);

    useEffect(()=>{
        if (touchpointIds){
            dispatch(fetchTouchpoints(touchpointIds));
        }
    }, [touchpointIds]);

    useEffect(() => {
      if (pageId) {
        if (!isSessionActive(pageId)) {
          handleEmailChange(email);
        } else {
            setIsAuthorized(true)
          dispatch(fetchPortfolioPage({ pageId, email })); // Session still active so fetch page again
        }
      }
    }, [email, pageId]);


    const handleEmailChange = (email) => {
        if (email) {
            dispatch(setEmail({ email, pageId }));
            dispatch(setPublicAccess(true))
            saveAccessInfo(email, pageId);
        }
    };

    const handleAgree = () => dispatch(markDisclaimerAsSeen())


    if (!disclaimerConfirmed) return <Disclaimer onAgree={handleAgree} />;

    if (!email && !isAuthorized) return (<PublicEntry handleEmailChange={handleEmailChange} />)



    const SideColumn = ()=>{
        return (
            <>
                {brandUrl && (
                    <div style={{height: 'auto', width: 150, background: "rgba(255, 255, 255, 0.5)", margin: 'auto'}}>
                        <Image src={`${GoogleCloudStorageBucketUrl}${brandUrl}`} height={'auto'} width={150} />
                    </div>
                )}
                <Divider/>
                <ThumbnailGrid files={files.filter(x=>x!==brandUrl).map(file=>`${GoogleCloudStorageBucketUrl}${file}`)}
                               direction={'column'}
                               divider={files.length > 1 ? <Divider/> : null}
                />
            </>
        )
    }

    return (
        <Layout style={{
            background: '#fff',
            minHeight: '100vh',
        }}>
            <PublicHeader extra={!screens.xl && <Button variant={'contained'} color={'info'} onClick={()=>setShowSide(true)}>Resources</Button>}/>
                <Layout.Content>
                    <div style={{padding: '0 0 0 20px'}}>
                        <Row justify={'space-between'}>
                            <Col xxl={{offset: 1}} flex={'1'} style={{ background: "transparent"}}>
                                <Row gutter={[32, 32]} align={'middle'} justify={'center'}>
                                    <Col xs={24} xl={16}>
                                         <Space direction={'vertical'} size={'large'}>
                                             <Typography.Text style={{color: "#000", fontSize: '1.5rem', fontFamily: "Quicksand, sans-serif", fontWeight: '500'}}>{portfolioPage?.title}</Typography.Text>
                                             <Typography.Text style={{color: "#000", fontFamily: "Quicksand, sans-serif", fontSize: '1.05rem', fontWeight: '400'}}>
                                                 {portfolioPage?.description?.length > 0 ? portfolioPage?.description : DefaultDescription}
                                             </Typography.Text>
                                         </Space>
                                    </Col>
                                    <Col xs={24} xl={8} className={'pt-4'}>
                                        <Card bordered={false} title={<Typography.Text>Apps to Try</Typography.Text>}>
                                            {filteredApps && (<WidgetContainer widgets={filteredApps} span={8}  />)}
                                        </Card>
                                    </Col>
                                </Row>
                                <Divider/>
                                {portfolioPage && Array.isArray(fullPortfolios) && fullPortfolios.length>0 ? (
                                    <Cheatsheet portfolios={fullPortfolios} />
                                ) : (
                                    <Result
                                        icon={<WarningSharp color={'error'} style={{fontSize: '50px'}} />}
                                        status={'warning'}
                                        title={'No Portfolios Found'}
                                        subTitle={'This page may be inactive, you may not have permission, or there are no portfolios to display.'}
                                    />
                                )}
                                <Divider/>
                            </Col>
                            {screens.xl && (
                                <Col flex={'0 0 300px'} className={'py-2 px-6 shadow-md round-2'} style={{marginLeft: '10px', borderBottomLeftRadius: 8}}>
                                    <SideColumn/>
                                </Col>
                            )
                            }
                        </Row>
                    </div>

                </Layout.Content>
            {!screens.xl && (
                <Drawer open={showSide} onClose={()=>setShowSide(false)}>
                    <SideColumn/>
                </Drawer>
            )}
            <Layout.Footer
                className={'dark-layout'}
                    style={{
                        height: '100px',
                        bottom: 0,
                        backdropFilter: 'blur(10px)', padding: '20px',
            }}
                >
                    <BottomDisclaimer/>
            </Layout.Footer>


        </Layout>
    );
};

export default PortfolioPagePublicEntrypoint;
